import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileAudio, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { uploadBatch, uploadFile, normalizeApiError } from '../services/api'
import { useAppStore } from '../stores/appStore'
import type { AudioFile } from '../types'

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB
const ACCEPTED_FORMATS = {
  'audio/wav': ['.wav'],
  'audio/mpeg': ['.mp3'],
  'audio/flac': ['.flac'],
  'audio/ogg': ['.ogg'],
  'audio/m4a': ['.m4a'],
  'audio/aac': ['.aac'],
}

interface FileUploadProps {
  className?: string
  role?: 'input' | 'reference'
  maxFiles?: number
  compact?: boolean
  onUploadComplete?: (files: AudioFile[]) => void
}

export default function FileUpload({
  className,
  role = 'input',
  maxFiles = 10,
  compact = false,
  onUploadComplete,
}: FileUploadProps) {
  const upsertFile = useAppStore((state) => state.upsertFile)

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: any[]) => {
      rejectedFiles.forEach(({ file, errors }: any) => {
        errors.forEach((error: any) => {
          if (error.code === 'file-too-large') {
            toast.error(`${file.name} is too large (max 100MB)`)
          } else if (error.code === 'file-invalid-type') {
            toast.error(`${file.name} is not a supported audio format`)
          }
        })
      })

      if (!acceptedFiles.length) return

      const toastId = toast.loading(
        acceptedFiles.length > 1 ? `Uploading ${acceptedFiles.length} files...` : 'Uploading file...'
      )

      try {
        let uploaded: AudioFile[] = []
        if (acceptedFiles.length === 1) {
          uploaded = [await uploadFile(acceptedFiles[0]!, role)]
        } else {
          uploaded = await uploadBatch(acceptedFiles, role)
        }
        uploaded.forEach(upsertFile)
        onUploadComplete?.(uploaded)
        toast.success(
          uploaded.length === 1
            ? `Uploaded ${uploaded[0]?.original_filename}`
            : `Uploaded ${uploaded.length} files`,
          { id: toastId }
        )
      } catch (error) {
        toast.error(normalizeApiError(error), { id: toastId, duration: 5000 })
      }
    },
    [onUploadComplete, role, upsertFile]
  )

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: ACCEPTED_FORMATS,
    maxSize: MAX_FILE_SIZE,
    multiple: maxFiles > 1,
    maxFiles,
  })

  return (
    <div {...getRootProps()} className={className}>
      <input {...getInputProps()} />
      <div
        className={[
          'upload-zone',
          compact ? 'upload-zone-compact' : '',
          isDragActive && !isDragReject ? 'upload-zone-active' : '',
          isDragReject ? 'upload-zone-reject' : '',
        ].join(' ')}
      >
        <div className="upload-icon-shell">
          <Upload className="w-7 h-7" />
        </div>
        <h3 className="upload-title">
          {isDragActive ? 'Drop files to upload' : role === 'reference' ? 'Upload reference track' : 'Upload source audio'}
        </h3>
        <p className="upload-copy">Drag files here or click to browse.</p>
        <div className="upload-meta">
          <FileAudio className="w-3.5 h-3.5" />
          <span>WAV, MP3, FLAC, OGG, M4A, AAC</span>
        </div>
        <div className="upload-meta">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Up to 100MB per file</span>
        </div>
      </div>
    </div>
  )
}
